import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Link2, Trash2, Pencil, FileText, Banknote, CreditCard, ArrowRightLeft, ScrollText, Calculator } from "lucide-react";
import { toast } from "sonner";
import Plate from "@/components/Plate";
import StatusBadge from "@/components/StatusBadge";
import DeletePaiementDialog from "@/components/DeletePaiementDialog";
import { useAuth } from "@/lib/auth";

// Date locale AAAA-MM-JJ (évite le décalage UTC de toISOString qui fait
// basculer le 1er du mois au 30 du mois précédent en fuseau UTC+1/+2)
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const emptyForm = {
  mode: "vente", // 'vente' or 'libre'
  vente_id: "",
  client_nom: "",
  montant: "",
  methode: "Espèces",
  date_paiement: isoDate(new Date()),
  notes: "",
};

export default function Paiements() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isCommercial = user?.role === "commercial";
  const [items, setItems] = useState([]);
  const [ventesMap, setVentesMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Add-payment dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedVente, setSelectedVente] = useState(null);

  // Sort: date-desc | date-asc | methode | montant-desc | montant-asc
  const [sortBy, setSortBy] = useState("date-desc");

  // Assign dialog (for free payments)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignQuery, setAssignQuery] = useState("");
  const [assignResults, setAssignResults] = useState([]);

  // Delete with password confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    montant: "", methode: "Espèces", date_paiement: "", notes: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    const [pRes, vRes] = await Promise.all([api.get("/paiements"), api.get("/ventes")]);
    setItems(pRes.data);
    const map = {};
    vRes.data.forEach((v) => { map[v.id] = v; });
    setVentesMap(map);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const total = useMemo(() => items.reduce((s, p) => s + Number(p.montant || 0), 0), [items]);
  const totalLibre = useMemo(
    () => items.filter((p) => !p.vente_id).reduce((s, p) => s + Number(p.montant || 0), 0),    [items]
  );
  const nbLibres = useMemo(() => items.filter((p) => !p.vente_id).length, [items]);

  // ---- Caisse : ventilation par méthode sur une date OU une période ----
  const todayISO = isoDate(new Date());
  const _now = new Date();
  const _firstOfMonth = isoDate(new Date(_now.getFullYear(), _now.getMonth(), 1));
  const _lastOfMonth = isoDate(new Date(_now.getFullYear(), _now.getMonth() + 1, 0));
  const [caisseMode, setCaisseMode] = useState("periode"); // "jour" | "periode" — défaut sur le mois en cours
  const [caisseDate, setCaisseDate] = useState(todayISO);
  const [caisseFrom, setCaisseFrom] = useState(_firstOfMonth);
  const [caisseTo, setCaisseTo] = useState(_lastOfMonth);
  const [caissePreset, setCaissePreset] = useState("mois"); // par défaut : le mois en cours

  const applyPreset = (p) => {
    setCaissePreset(p);
    const now = new Date();
    const iso = isoDate;
    if (p === "aujourdhui") {
      setCaisseMode("jour"); setCaisseDate(iso(now)); return;
    }
    if (p === "semaine") {
      const day = now.getDay() || 7; // Mon=1..Sun=7
      const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      setCaisseMode("periode"); setCaisseFrom(iso(monday)); setCaisseTo(iso(sunday)); return;
    }
    if (p === "mois") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setCaisseMode("periode"); setCaisseFrom(iso(first)); setCaisseTo(iso(last)); return;
    }
    if (p === "annee") {
      setCaisseMode("periode"); setCaisseFrom(`${now.getFullYear()}-01-01`); setCaisseTo(`${now.getFullYear()}-12-31`); return;
    }
  };

  // When user picks a specific month via the month input (YYYY-MM), snap the range to that month
  const applyMonthYM = (ym) => {
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return;
    const [yy, mm] = ym.split("-").map(Number);
    const first = new Date(yy, mm - 1, 1);
    const last = new Date(yy, mm, 0);
    const iso = isoDate;
    setCaisseMode("periode");
    setCaissePreset("mois");
    setCaisseFrom(iso(first));
    setCaisseTo(iso(last));
  };

  const caisse = useMemo(() => {
    const inRange = (p) => {
      const d = (p.date_paiement || "").slice(0, 10);
      if (!d) return false;
      if (caisseMode === "jour") return d === caisseDate;
      return d >= caisseFrom && d <= caisseTo;
    };
    const rangeItems = items.filter(inRange);
    const methods = ["Espèces", "Carte", "Virement", "Chèque"];
    const byMethod = {};
    let dayTotal = 0;
    methods.forEach((m) => { byMethod[m] = { count: 0, total: 0 }; });
    byMethod.Autre = { count: 0, total: 0 };
    rangeItems.forEach((p) => {
      const m = methods.includes(p.methode) ? p.methode : "Autre";
      const amt = Number(p.montant || 0);
      byMethod[m].count += 1;
      byMethod[m].total += amt;
      dayTotal += amt;
    });
    return { dayItems: rangeItems, byMethod, dayTotal };
  }, [items, caisseMode, caisseDate, caisseFrom, caisseTo]);

  const caisseRangeLabel = useMemo(() => {
    if (caisseMode === "jour") {
      return dateFR(caisseDate);
    }
    return `${dateFR(caisseFrom)} → ${dateFR(caisseTo)}`;
  }, [caisseMode, caisseDate, caisseFrom, caisseTo]);

  // Sorted items based on sortBy state
  const sortedItems = useMemo(() => {
    const arr = [...items];
    switch (sortBy) {
      case "date-asc":
        arr.sort((a, b) => (a.date_paiement || "").localeCompare(b.date_paiement || ""));
        break;
      case "methode":
        arr.sort((a, b) =>
          (a.methode || "").localeCompare(b.methode || "", "fr") ||
          (b.date_paiement || "").localeCompare(a.date_paiement || "")
        );
        break;
      case "montant-desc":
        arr.sort((a, b) => Number(b.montant || 0) - Number(a.montant || 0));
        break;
      case "montant-asc":
        arr.sort((a, b) => Number(a.montant || 0) - Number(b.montant || 0));
        break;
      case "date-desc":
      default:
        arr.sort((a, b) => (b.date_paiement || "").localeCompare(a.date_paiement || ""));
    }
    return arr;
  }, [items, sortBy]);

  const runSearch = async (q, setter) => {
    setSearching(true);
    try {
      const { data } = await api.get("/ventes/search/quick", { params: { q } });
      setter(data);
    } finally {
      setSearching(false);
    }
  };

  const openAdd = () => {
    setForm(emptyForm);
    setSelectedVente(null);
    setQuery("");
    setResults([]);
    setOpen(true);
    setTimeout(() => runSearch("", setResults), 0);
  };

  const selectVente = (v) => {
    setSelectedVente(v);
    setForm((f) => ({ ...f, vente_id: v.id }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return; // anti double-clic : évite les paiements en double
    if (form.mode === "vente" && !form.vente_id) {
      toast.error("Sélectionnez une vente, ou passez en mode 'Paiement libre'");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        vente_id: form.mode === "libre" ? null : form.vente_id,
        client_nom: form.mode === "libre" ? form.client_nom : (selectedVente?.client_nom || ""),
        montant: Number(form.montant || 0),
        methode: form.methode,
        date_paiement: form.date_paiement,
        notes: form.notes,
      };
      await api.post("/paiements", payload);
      toast.success(payload.vente_id ? "Paiement lié à la vente" : "Acompte libre enregistré");
      setOpen(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Assign a free payment to a vente
  const openAssign = (paiement) => {
    setAssignTarget(paiement);
    setAssignQuery(paiement.client_nom || "");
    setAssignResults([]);
    setAssignOpen(true);
    setTimeout(() => runSearch(paiement.client_nom || "", setAssignResults), 0);
  };

  const doAssign = async (vente) => {
    try {
      await api.post(`/paiements/${assignTarget.id}/affecter`, null, { params: { vente_id: vente.id } });
      toast.success(`Paiement affecté à ${vente.plaque}`);
      setAssignOpen(false);
      load();
    } catch (err) {
      toast.error("Erreur lors de l'affectation");
    }
  };

  const detach = async (p) => {
    if (!window.confirm("Détacher ce paiement de sa vente (le rendre libre) ?")) return;
    await api.post(`/paiements/${p.id}/detacher`);
    toast.success("Paiement détaché");
    load();
  };

  const remove = (p) => {
    setDeleteTarget(p);
    setDeleteOpen(true);
  };

  const openEdit = (p) => {
    setEditTarget(p);
    setEditForm({
      montant: String(p.montant ?? ""),
      methode: p.methode || "Espèces",
      date_paiement: p.date_paiement || isoDate(new Date()),
      notes: p.notes || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      const payload = {
        montant: Number(editForm.montant || 0),
        methode: editForm.methode,
        date_paiement: editForm.date_paiement,
        notes: editForm.notes,
      };
      await api.put(`/paiements/${editTarget.id}`, payload);
      toast.success("Paiement mis à jour");
      setEditOpen(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de la mise à jour");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Encaissements
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Paiements</h1>
          {isAdmin ? (
            <p className="text-sm text-muted-foreground mt-2" data-testid="paiements-total-line">
              Total encaissé : <span className="font-display font-bold text-foreground">{money(total)}</span>
              {nbLibres > 0 && (
                <> · <span className="text-amber-700 font-semibold">{nbLibres} acompte(s) libre(s) — {money(totalLibre)}</span></>
              )}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-2">
              {items.length} paiement(s) enregistré(s)
              {nbLibres > 0 && (
                <> · <span className="text-amber-700 font-semibold">{nbLibres} acompte(s) libre(s)</span></>
              )}
            </p>
          )}
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} data-testid="paiement-add-btn" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
              <Plus size={14} className="mr-1" /> Nouveau paiement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                Nouveau paiement
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              {/* Mode toggle */}
              <div className="flex gap-2 p-1 bg-slate-100 rounded-sm" data-testid="paiement-mode-toggle">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, mode: "vente" })}
                  data-testid="paiement-mode-vente"
                  className={`flex-1 px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                    form.mode === "vente" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"
                  }`}
                >
                  Lié à une vente
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, mode: "libre" })}
                  data-testid="paiement-mode-libre"
                  className={`flex-1 px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                    form.mode === "libre" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"
                  }`}
                >
                  Paiement libre / acompte
                </button>
              </div>

              {form.mode === "vente" ? (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Lier à une vente (plaque ou client) *</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="relative flex-1">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        data-testid="paiement-search-input"
                        type="search"
                        name="paiement-search"
                        autoComplete="off"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(query, setResults); } }}
                        placeholder="Rechercher…"
                        className="pl-9 rounded-sm"
                      />
                    </div>
                    <Button type="button" onClick={() => runSearch(query, setResults)} disabled={searching} variant="outline" className="rounded-sm" data-testid="paiement-search-btn">
                      {searching ? "…" : "Chercher"}
                    </Button>
                  </div>

                  {selectedVente && (
                    <div className="mt-3 p-3 border border-klein/30 bg-blue-50/40 rounded-sm flex items-center justify-between" data-testid="paiement-selected-vente">
                      <div className="flex items-center gap-3">
                        <Plate value={selectedVente.plaque} small />
                        <div className="text-sm">
                          <div className="font-medium">{selectedVente.marque} {selectedVente.modele} — {selectedVente.client_nom}</div>
                          <div className="text-xs text-muted-foreground">
                            Prix {money(selectedVente.prix_vente)} · Restant {money(selectedVente.montant_restant)}
                          </div>
                        </div>
                      </div>
                      <StatusBadge statut={selectedVente.statut} />
                    </div>
                  )}

                  {!selectedVente && results.length > 0 && (
                    <div className="mt-2 max-h-56 overflow-y-auto border border-border rounded-sm divide-y" data-testid="paiement-search-results">
                      {results.map((v) => (
                        <button
                          type="button"
                          key={v.id}
                          onClick={() => selectVente(v)}
                          data-testid={`paiement-result-${v.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Plate value={v.plaque} small />
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{v.marque} {v.modele} — {v.client_nom}</div>
                              <div className="text-xs text-muted-foreground">{dateFR(v.date_vente)} · Restant {money(v.montant_restant)}</div>
                            </div>
                          </div>
                          <StatusBadge statut={v.statut} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 border border-amber-200 bg-amber-50/40 rounded-sm">
                  <div className="text-sm font-medium text-amber-900 mb-2">Paiement libre (acompte)</div>
                  <p className="text-xs text-amber-800 mb-3">
                    Ce paiement ne sera lié à aucune vente. Vous pourrez l'affecter plus tard à une vente en cliquant sur "Affecter" dans la liste.
                  </p>
                  <Label className="text-xs uppercase tracking-wider">Nom du client (facultatif)</Label>
                  <Input
                    data-testid="paiement-libre-client"
                    value={form.client_nom}
                    onChange={(e) => setForm({ ...form, client_nom: e.target.value })}
                    placeholder="Ex: Jean Dupont"
                    className="mt-1 rounded-sm bg-white"
                  />
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Montant *</Label>
                  <Input data-testid="paiement-add-montant" type="number" inputMode="decimal" step="0.01" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} required className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Méthode</Label>
                  <Select value={form.methode} onValueChange={(v) => setForm({ ...form, methode: v })}>
                    <SelectTrigger data-testid="paiement-add-methode" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Espèces">Espèces</SelectItem>
                      <SelectItem value="Virement">Virement</SelectItem>
                      <SelectItem value="Chèque">Chèque</SelectItem>
                      <SelectItem value="Carte">Carte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Date</Label>
                  <Input data-testid="paiement-add-date" type="date" value={form.date_paiement} onChange={(e) => setForm({ ...form, date_paiement: e.target.value })} className="mt-1 rounded-sm" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider">Notes</Label>
                <Textarea data-testid="paiement-add-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 rounded-sm" />
              </div>

              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="paiement-add-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                  {saving ? "Enregistrement…" : "Enregistrer le paiement"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {/* Caisse — visible uniquement pour l'admin */}
      {isAdmin && (
      <div className="rounded-sm border border-border bg-white" data-testid="caisse-du-jour">
        <div className="flex flex-col gap-3 px-4 py-3 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-sm bg-klein text-white flex items-center justify-center">
                <Calculator size={16} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Caisse · {caisseMode === "jour" ? "Jour" : "Période"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {caisse.dayItems.length} paiement(s) sur <span className="font-semibold">{caisseRangeLabel}</span> · Total <span className="font-bold text-klein">{money(caisse.dayTotal)}</span>
                </div>
              </div>
            </div>
            {/* Presets rapides */}
            <div className="flex flex-wrap gap-1" data-testid="caisse-presets">
              {[
                { key: "aujourdhui", label: "Aujourd'hui" },
                { key: "semaine", label: "Semaine" },
                { key: "mois", label: "Mois" },
                { key: "annee", label: "Année" },
                { key: "custom", label: "Personnalisé" },
              ].map((p) => (
                <Button
                  key={p.key}
                  type="button"
                  variant={caissePreset === p.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => applyPreset(p.key)}
                  className={`rounded-sm h-8 text-xs ${caissePreset === p.key ? "bg-klein hover:bg-blue-800 text-white" : ""}`}
                  data-testid={`caisse-preset-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
          {/* Sélecteurs de date (jour ou période) */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-1 cursor-pointer text-xs">
              <input
                type="radio"
                name="caisse-mode"
                checked={caisseMode === "jour"}
                onChange={() => { setCaisseMode("jour"); setCaissePreset("custom"); }}
                data-testid="caisse-mode-jour"
              />
              <span className="uppercase tracking-wider">Un jour</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer text-xs">
              <input
                type="radio"
                name="caisse-mode"
                checked={caisseMode === "periode"}
                onChange={() => { setCaisseMode("periode"); setCaissePreset("custom"); }}
                data-testid="caisse-mode-periode"
              />
              <span className="uppercase tracking-wider">Période</span>
            </label>
            <div className="flex-1" />
            {caisseMode === "jour" ? (
              <Input
                type="date"
                value={caisseDate}
                onChange={(e) => { setCaisseDate(e.target.value); setCaissePreset("custom"); }}
                className="rounded-sm h-9 w-40"
                data-testid="caisse-date"
              />
            ) : caissePreset === "mois" ? (
              <Input
                type="month"
                value={caisseFrom.slice(0, 7)}
                onChange={(e) => applyMonthYM(e.target.value)}
                className="rounded-sm h-9 w-44"
                data-testid="caisse-month-picker"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={caisseFrom}
                  onChange={(e) => { setCaisseFrom(e.target.value); setCaissePreset("custom"); }}
                  className="rounded-sm h-9 w-40"
                  data-testid="caisse-from"
                />
                <span className="text-muted-foreground text-xs">au</span>
                <Input
                  type="date"
                  value={caisseTo}
                  onChange={(e) => { setCaisseTo(e.target.value); setCaissePreset("custom"); }}
                  className="rounded-sm h-9 w-40"
                  data-testid="caisse-to"
                />
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {[
            { key: "Espèces", icon: Banknote, color: "text-emerald-700", bg: "bg-emerald-50" },
            { key: "Carte", icon: CreditCard, color: "text-blue-700", bg: "bg-blue-50" },
            { key: "Virement", icon: ArrowRightLeft, color: "text-indigo-700", bg: "bg-indigo-50" },
            { key: "Chèque", icon: ScrollText, color: "text-amber-700", bg: "bg-amber-50" },
          ].map(({ key, icon: Icon, color, bg }) => {
            const row = caisse.byMethod[key] || { count: 0, total: 0 };
            return (
              <div key={key} className="p-3 flex items-center gap-3" data-testid={`caisse-method-${key.toLowerCase()}`}>
                <div className={`w-9 h-9 rounded-sm ${bg} ${color} flex items-center justify-center flex-shrink-0`}>
                  <Icon size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{key}</div>
                  <div className={`font-display font-black text-xl tabular-nums leading-tight ${row.total > 0 ? "text-foreground" : "text-muted-foreground"}`}>
                    {money(row.total)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {row.count} paiement{row.count > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Toolbar: sort */}
      <div className="flex items-center justify-end gap-2 text-sm" data-testid="paiements-toolbar">
        <label htmlFor="pay-sort" className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          Trier par
        </label>
        <select
          id="pay-sort"
          data-testid="paiements-sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="h-9 rounded-sm border border-input bg-background px-3 text-sm"
        >
          <option value="date-desc">Date · plus récent</option>
          <option value="date-asc">Date · plus ancien</option>
          <option value="methode">Méthode (A → Z)</option>
          <option value="montant-desc">Montant · décroissant</option>
          <option value="montant-asc">Montant · croissant</option>
        </select>
      </div>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Affecter ce paiement à une vente</DialogTitle>
          </DialogHeader>
          {assignTarget && (
            <div className="text-sm text-muted-foreground mb-2">
              Paiement de <span className="font-semibold text-foreground">{money(assignTarget.montant)}</span> ({assignTarget.methode})
              {assignTarget.client_nom ? ` — ${assignTarget.client_nom}` : ""}
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="paiement-assign-input"
                type="search"
                name="paiement-assign-search"
                autoComplete="off"
                value={assignQuery}
                onChange={(e) => setAssignQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(assignQuery, setAssignResults); } }}
                placeholder="Rechercher…"
                className="pl-9 rounded-sm"
              />
            </div>
            <Button type="button" onClick={() => runSearch(assignQuery, setAssignResults)} variant="outline" className="rounded-sm" data-testid="paiement-assign-search-btn">
              Chercher
            </Button>
          </div>
          <div className="max-h-72 overflow-y-auto border border-border rounded-sm divide-y mt-2" data-testid="paiement-assign-results">
            {assignResults.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">Aucun résultat.</div>
            )}
            {assignResults.map((v) => (
              <button
                type="button"
                key={v.id}
                onClick={() => doAssign(v)}
                data-testid={`paiement-assign-pick-${v.id}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Plate value={v.plaque} small />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{v.marque} {v.modele} — {v.client_nom}</div>
                    <div className="text-xs text-muted-foreground">{dateFR(v.date_vente)} · Restant {money(v.montant_restant)}</div>
                  </div>
                </div>
                <StatusBadge statut={v.statut} />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Actions</th>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Méthode</th>
              <th className="px-6 py-3">Affectation</th>
              <th className="px-6 py-3">Client</th>
              <th className="px-6 py-3 text-right">Montant</th>
              <th className="px-6 py-3">Détail</th>
            </tr>
          </thead>
          <tbody data-testid="paiements-table-body">
            {loading && <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">Aucun paiement.</td></tr>
            )}
            {sortedItems.map((p) => {
              const v = p.vente_id ? ventesMap[p.vente_id] : null;
              const libre = !p.vente_id;
              return (
                <tr key={p.id} className={`border-t hover:bg-slate-50 transition-colors align-top ${libre ? "bg-amber-50/30" : ""}`}>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`paiement-edit-${p.id}`}
                      title="Modifier le détail"
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-klein/5 hover:bg-klein/10 text-klein border border-klein/20 rounded-sm mr-1 font-semibold"
                    >
                      <Pencil size={12} /> Modifier
                    </button>
                    {libre ? (
                      <button onClick={() => openAssign(p)} data-testid={`paiement-assign-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-sm mr-1">
                        <Link2 size={12} /> Affecter
                      </button>
                    ) : (
                      <button onClick={() => detach(p)} data-testid={`paiement-detach-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-slate-100 rounded-sm mr-1">
                        Détacher
                      </button>
                    )}
                    <button onClick={() => remove(p)} data-testid={`paiement-remove-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-red-50 rounded-sm">
                      <Trash2 size={12} />
                    </button>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">{dateFR(p.date_paiement)}</td>
                  <td className="px-6 py-3">{p.methode}</td>
                  <td className="px-6 py-3">
                    {libre ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                        Acompte libre
                      </span>
                    ) : v ? (
                      <div className="flex items-center gap-2">
                        <Plate value={v.plaque} small />
                        <Link to={`/ventes/${v.id}`} className="text-klein hover:underline text-sm">
                          {v.marque} {v.modele}
                        </Link>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-6 py-3">{v ? v.client_nom : (p.client_nom || "—")}</td>
                  <td className="px-6 py-3 text-right font-mono">{money(p.montant)}</td>
                  <td className="px-6 py-3 max-w-xs">
                    {p.notes ? (
                      <div className="flex items-start gap-1.5 text-xs text-slate-700" data-testid={`paiement-notes-${p.id}`}>
                        <FileText size={12} className="mt-0.5 text-muted-foreground flex-shrink-0" />
                        <span className="whitespace-pre-wrap break-words">{p.notes}</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`paiement-add-note-${p.id}`}
                        className="text-xs text-muted-foreground hover:text-klein hover:underline italic"
                      >
                        + Ajouter un détail
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit paiement dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="paiement-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Modifier le paiement</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Montant *</Label>
                <Input
                  data-testid="paiement-edit-montant"
                  type="number" inputMode="decimal"
                  step="0.01"
                  required
                  value={editForm.montant}
                  onChange={(e) => setEditForm({ ...editForm, montant: e.target.value })}
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Méthode</Label>
                <Select value={editForm.methode} onValueChange={(v) => setEditForm({ ...editForm, methode: v })}>
                  <SelectTrigger data-testid="paiement-edit-methode" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Espèces">Espèces</SelectItem>
                    <SelectItem value="Virement">Virement</SelectItem>
                    <SelectItem value="Chèque">Chèque</SelectItem>
                    <SelectItem value="Carte">Carte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Date</Label>
              <Input
                data-testid="paiement-edit-date"
                type="date"
                value={editForm.date_paiement}
                onChange={(e) => setEditForm({ ...editForm, date_paiement: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Détail / Notes</Label>
              <Textarea
                data-testid="paiement-edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={4}
                placeholder="Ex: Acompte sur véhicule, N° chèque 123456, référence virement…"
                className="mt-1 rounded-sm"
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Toute info utile : numéro de chèque, référence virement, contexte du paiement.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={savingEdit}
                data-testid="paiement-edit-submit"
                className="bg-klein hover:bg-blue-800 text-white rounded-sm"
              >
                {savingEdit ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeletePaiementDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        paiement={deleteTarget}
        onDeleted={load}
      />
    </div>
  );
}
